<template>
  <div class="card briefcase-horizontal">
    <div class="header-line">
      <div class="fs22">
        {{ $t("rightPanel.briefcase.label") }}
      </div>
      <div>
        <span
          v-if="isDataLoadingStatePool"
          class="fs22 amount-balance"
        >
          <AuDecimalStatic
            :value="fullSum"
            :decimal="2"
            :indent="2"
            :addition="defaultTokenMnemonic"
          />
        </span>
        <span
          v-else
          class="fs22 amount-balance"
        >
          <AuSkeleton
            width="200px"
            height="25px"
          />
        </span>
      </div>
    </div>
    <div class="middle-line">
      <div v-if="isDataLoadingState">
        <div>
          <Apexchart
            v-if="!chartLoading && balance"
            :options="options"
            :series="mappedSeries"
            height="200"
          />
        </div>
        <div class="no-assets">
          <Apexchart
            v-if="!chartLoading && !balance"
            :options="optionsNoAssets"
            :series="mappedSeries"
            height="200"
          />
        </div>
      </div>

      <div
        v-else
        class="no-apexchart"
      />
      <div class="footer-line">
        <div
          v-if="loginAsTester"
          class="test-balance"
        >
          <div class="test-balance">
            Assets:
            <AuDecimalStatic
              :value="dataAssetsBalance"
              :indent="2"
              :show-addition="false"
            />
            Reserved:
            <AuDecimalStatic
              :value="dataAssets?.assetsReserved"
              :indent="2"
              :show-addition="false"
            />
            Total:
            <AuDecimalStatic
              :value="totalAssets"
              :indent="2"
              :show-addition="false"
            />
          </div>
          <div>
            Project Invest:
            <AuDecimalStatic
              :value="dataInvestFront"
              :indent="2"
              :show-addition="false"
            />
            Stacking Pool:
            <AuDecimalStatic
              :value="dataStacking"
              :indent="2"
              :show-addition="false"
            />
          </div>
        </div>
        <div class="sub-header">
          <RouterLink
            :to="{ path: '/my-assets', query: { selectedTitle: 'My orders' } }"
          >
            {{ $t(`briefcase.amountInvest${postfix}`) }}
            <span v-if="dataInvestBack">
              <AuDecimalStatic
                :value="dataInvestBack"
                :indent="2"
                :addition="defaultTokenMnemonic"
              />
            </span>
            <span v-else>
              <AuSkeleton />
            </span>
          </RouterLink>
        </div>
        <div class="button-block">
          <AuButton
            type="primary"
            border-color="#2aba7c"
            center
            @click="goToDeposit"
          >
            {{ $t("briefcase.buy") }}
          </AuButton>
          <AuButton
            type="primary"
            border-color="#2aba7c"
            center
            @click="goToWithdraw"
          >
            {{ $t("briefcase.withdraw") }}
          </AuButton>
          <AuButton
            v-if="isSendBtn"
            type="primary"
            border-color="#2aba7c"
            center
            @click="goToSend"
          >
            {{ $t("briefcase.sendAction") }}
          </AuButton>
          <!-- <AuDropdown
          v-model="paramsDropdown"
          class="dropdown-flex"
          :options="optionsDropdown"
          :placeholder="$t('briefcase.more')"
          @select="handleSelect"
        /> -->
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from "lodash";
import CurrencySupport from "@/help/currencySupport";
import { checkAccess, checkPostfix, scope } from "@/help/accessData";
import {
  getDalyBalanceInvest,
  getPrice,
  getSumInvest,
  getSumStacking,
  usersBalances
} from "@/help/usersFinances";
import { mapActions, mapGetters } from "vuex";

export default {
  data() {
    return {
      heightInPixels: "20",
      currencySup: new CurrencySupport(),
      loading: false,
      fullSum: null,
      balance: 0,
      series: [],
      mappedSeries: [],
      dataOrders: [],
      totalSumInvest: null,
      width: null,
      chartLoading: false,
      defaultTokenMnemonicId: null,
      paramsDropdown: null,
      dataAssets: null,
      dataInvestFront: null,
      dataInvestBack: null,
      dataStacking: null,
      totalAssets: null,
      optionsDropdown: [
        { display: this.$t("briefcase.operations"), value: "operations" },
        { display: this.$t("briefcase.extract"), value: "extract" }
      ]
    };
  },

  computed: {
    ...mapGetters({
      auOwners: "owner/auOwners",
      myProjectsOrders: "capitals/myProjectsOrders",
      myStackingOrders: "events/myStackingOrders",
      isTestRole: "profile/isTestRole",
      tokensFiat: "tokensFiat",
      tokensGold: "tokensGold",
      tokensDao: "tokensDao",
      tokensUtility: "tokensUtility",
      tokens: "tokens",
      defaultTokenMnemonic: "defaultToken/defaultTokenMnemonic",
      apiSystemSettings: "apiSystemSettings",
      exchangeBalances: "exchangeBalances",
      defaultTokenEnv: "defaultToken/defaultTokenEnv",
      dailyStatsBack: "dailyStatsBack",
      checkProfile: "profile/checkProfile"
    }),

    postfix() {
      return checkPostfix();
    },

    isNotRwa() {
      return checkAccess(scope.notRwa);
    },

    cssVars() {
      return {
        "--scroll": this.labels?.length < 9 ? "hidden" : "scroll"
      };
    },

    isSendBtn() {
      return (
        this.apiSystemSettings?.enableUserSend == "true" &&
        this.defaultTokenMnemonicId
      );
    },

    loginAsTester() {
      return this.isTestRole;
    },

    defaultTokenDecimals() {
      return (
        _.find(
          this.tokens,
          (token) => token.mnemonicEng === this.defaultTokenMnemonic
        )?.decimals ?? 2
      );
    },

    isDataLoadingState() {
      return this.auOwners != null;
    },

    isDataLoadingStatePool() {
      return this.auOwners != null;
    },

    isLightTheme() {
      return this.$store.getters.isLightTheme;
    },

    colorLabel() {
      return this.isLightTheme
        ? Array(9).fill("#080808")
        : Array(9).fill("#fff");
    },

    labels() {
      let resp = [];
      if (this.auOwners?.length) {
        this.series.forEach((series) => {
          const owner = _.find(
            this.auOwners,
            (owner) => owner.auToken.id == series.id
          );

          if (owner?.auToken.mnemonicEng == this.defaultTokenEnv) {
            resp.push(owner?.auToken.mnemonicEng);
          }
          else {
            resp.push(
              `<span class="legend">${
                this.defaultTokenEnv
              }: <b>${this.convertNumber(
                this.convertPrice(owner).mainTokenPrice
              )}</b><br/></span>${owner?.auToken.mnemonicEng}`
            );
          }
        });
      }
      if (!resp.length) {
        resp = [];
        resp.push(this.$t("rightPanel.briefcase.noAssets"));
      }
      return resp;
    },

    options: {
      get: function () {
        return {
          labels: this.labels,
          chart: {
            type: "donut",
            show: false,
            hidden: true
          },
          pie: {
            expandOnClick: false,
            donut: {
              background: "transparent",
              labels: {
                show: false,
                total: {
                  showAlways: false,
                  show: false
                }
              }
            }
          },
          colors: [
            "#299667",
            "#9720F6",
            "#E2A64D",
            "#489EE1",
            "#294896",
            "#35d5ec",
            "#E2684D",
            "#489EE1",
            "#C3B3D0"
          ],
          dataLabels: {
            enabled: false
          },
          tooltip: {
            enabled: true,
            y: {
              formatter(value) {
                if (value == 0) {
                  return 0;
                }

                value = Math.round(value * 100) / 100;

                let formattedPrice;
                if (value % 1 === 0) {
                  formattedPrice = value
                    .toLocaleString("ru-RU")
                    .replace(",", ".");
                }
                else {
                  formattedPrice = value
                    .toLocaleString("ru-RU", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })
                    .replace(",", ".");
                }
                return formattedPrice;
              }
            }
          },
          legend: {
            show: true,
            position: "right",
            height: this.heightInPixels,
            fontSize: "10px",
            fontFamily: "Suisse Intl",
            labels: {
              colors: this.colorLabel
            },
            markers: {
              width: 8,
              height: 8
            }
          }
        };
      },
      set: function (newValue) {
        return newValue;
      }
    },
    optionsNoAssets: {
      get: function () {
        return {
          labels: this.labels,
          chart: {
            type: "donut",
            show: false,
            hidden: true
          },
          pie: {
            expandOnClick: false,
            donut: {
              background: "transparent",
              labels: {
                show: false,
                total: {
                  showAlways: false,
                  show: false
                }
              }
            }
          },
          colors: ["#c6c6c6"],
          dataLabels: {
            enabled: false
          },
          tooltip: {
            enabled: false
          },
          legend: {
            show: true,
            position: "right",
            height: "20",
            fontSize: "10px",
            fontFamily: "Suisse Intl",
            labels: {
              colors: this.colorLabel
            },
            markers: {
              width: 8,
              height: 8
            }
          }
        };
      },
      set: function (newValue) {
        return newValue;
      }
    }
  },

  watch: {
    dailyStatsBack: {
      immediate: true,
      async handler() {
        const { sumBalance, sumInvest } = await getDalyBalanceInvest(
          this.dailyStatsBack,
          this.defaultTokenDecimals
        );
        this.dataAssetsBalance = sumBalance;
        this.dataInvestFront = sumInvest;
      }
    },
    loginAsTester: {
      immediate: true,
      handler: async function (newVal) {
        if (newVal === true) {
          await this.checkUserBalanse();
        }
      }
    },
    auOwners: {
      immediate: true,
      handler() {
        if (this.auOwners) {
          this.dataAssets = usersBalances(this.auOwners, this.currencySup);
          this.checkBalance(this.auOwners);
          this.checkTokenMnemonicId(this.auOwners);
        }
      }
    },
    defaultTokenMnemonic: {
      immediate: true,
      handler() {
        if (this.auOwners) {
          this.checkBalance(this.auOwners);
          this.checkTokenMnemonicId(this.auOwners);
        }
      }
    },
    checkProfile: {
      immediate: true,
      async handler() {
        await this.getAuOwners();
        this.legendWidth();
        await this.checkUserBalanse();
      }
    }
  },

  async mounted() {
    window.addEventListener("resize", this.resizeHandler);
    window.addEventListener("resize", this.legendWidth);
    this.$nextTick(() => {
      this.legendWidth();
    });
    await this.getUserOrders();
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.legendWidth);
    window.removeEventListener("resize", this.resizeHandler);
  },

  methods: {
    ...mapActions({
      getAuOwners: "owner/getAuOwners",
      getStackingPoolNoPagination: "events/getStackingPoolNoPagination",
      getInvestmentsNoPagination: "capitals/getInvestmentsNoPagination",
      getWithdrawalRequests: "withdrawal/getWithdrawalRequests",
      getInvestmentsAuCapitals: "capitals/getInvestmentsAuCapitals"
    }),

    async checkUserBalanse() {
      this.dataAssets = usersBalances(this.auOwners, this.currencySup);
      this.dataInvestBack = null;

      await this.getInvestmentsNoPagination();
      this.dataInvestBack = getSumInvest(
        this.myProjectsOrders,
        this.defaultTokenDecimals
      );
      this.dataAssetsBalance = this.dataAssets?.balance;

      this.totalAssets =
        parseFloat(this.dataAssets?.balance) -
        parseFloat(this.dataAssets?.assetsReserved) +
        parseFloat(this.dataInvestBack);

      await this.getStackingPoolNoPagination();
      this.dataStacking = getSumStacking(
        this.myStackingOrders,
        this.defaultTokenDecimals
      );
    },

    handleSelect(selectedOption) {
      console.log("selectedOption", selectedOption);
    },

    goToDeposit() {
      if (!this.isNotRwa) {
        this.$router.push({ name: "card buy form" });
      }
      else {
        this.$router.push({ name: "buy form" });
      }
    },

    goToWithdraw() {
      if (!this.isNotRwa) {
        this.$router.push({ name: "card withdraw form" });
      }
      else {
        this.$router.push({ name: "withdraw" });
      }
    },

    goToSend() {
      if (!this.isSendBtn) {
        return;
      }
      this.$router.push({
        name: "send token",
        params: { id: this.defaultTokenMnemonicId }
      });
    },

    async changeType(type) {
      this.type = type;
    },

    resizeHandler() {
      this.chartLoading = true;
      this.$nextTick(() => {
        this.chartLoading = false;
      });
    },
    legendWidth() {
      const legend = document.querySelector(".apexcharts-legend");
      if (legend) {
        const parentWidth = legend.parentElement.clientWidth;
        const widthPixels = parentWidth * 0.7;

        if (widthPixels < 230) {
          this.heightInPixels = "40";
        }
        else {
          if (this.series.length < 4) {
            this.heightInPixels = "20";
          }
          if (this.series.length > 4 && this.series.length < 7) {
            this.heightInPixels = "40";
          }
          if (this.series.length > 7) {
            this.heightInPixels = "80";
          }
        }
      }
    },
    changeChart(chartContext, _config) {
      this.$nextTick(() => {
        chartContext.pie.centerX = 75;
      });
    },

    convertPrice(owner) {
      return getPrice(owner, this.currencySup);
    },

    convertNumber(price) {
      if (price == 0) {
        return 0;
      }

      price = Math.round(price * 100) / 100;

      let formattedPrice;
      if (price % 1 === 0) {
        formattedPrice = price.toLocaleString("ru-RU").replace(",", ".");
      }
      else {
        formattedPrice = price
          .toLocaleString("ru-RU", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })
          .replace(",", ".");
      }
      return formattedPrice;
    },

    checkTokenMnemonicId(value) {
      const found = _.find(
        value,
        (item) =>
          _.get(item, "auToken.mnemonicEng") == this.defaultTokenMnemonic
      );

      this.defaultTokenMnemonicId = found?.auToken?.id;
    },

    checkBalance(value) {
      const { series, mappedSeries, balance } = usersBalances(
        value,
        this.currencySup
      );

      // забираем балансы и инвестиции с back
      const { sumBalance, sumInvest } = getDalyBalanceInvest(
        this.dailyStatsBack,
        this.defaultTokenDecimals
      );

      this.series = series;
      this.mappedSeries = mappedSeries;
      this.fullSum = parseFloat(sumBalance) + parseFloat(sumInvest);

      this.balance = balance;

      if (!this.fullSum) {
        this.mappedSeries = [];
        this.mappedSeries.push(1);
      }
    },

    async getUserOrders() {
      const { sumInvest } = getDalyBalanceInvest(
        this.dailyStatsBack,
        this.defaultTokenDecimals
      );
      this.totalSumInvest = sumInvest;
    }
  }
};
</script>

<style lang="scss">
.briefcase-horizontal {
  display: flex;
  flex-direction: column;
  .no-apexchart {
    min-height: 110px;
    @media screen and (max-width: 900px) {
      min-height: 202px;
    }
    @media screen and (max-width: 590px) {
      min-height: 90px;
    }
    @media screen and (max-width: 461px) {
      min-height: 80px;
    }
    @media screen and (max-width: 414px) {
      min-height: 65px;
    }
    @media screen and (max-width: 376px) {
      min-height: 61px;
    }
  }
  .middle-line {
    .vue-apexcharts {
      .apexcharts-canvas {
        .apexcharts-svg {
          foreignObject {
            .apexcharts-legend.apx-legend-position-right {
              right: 0 !important;
              top: 50% !important;
              transform: translateY(-50%);

              right: 0 !important;
              top: 50% !important;
              transform: translateY(-50%);

              width: 70%;
              height: 40px;
              display: grid;
              grid-template-columns: repeat(auto-fill, minmax(70px, 1fr));
              gap: 1px 0;
              grid-auto-rows: 18px;
              // overflow-y: var(--scroll);
              overflow-x: hidden;

              // альтернатива
              // display: flex;
              // flex-direction: row;
              // justify-content: start;
              // align-items: center;
              // align-self: center;
              // flex-wrap: wrap;
              // max-width: 70%;
              // height: 60px;
              // padding-left: 10px;
              // margin-bottom: 10px;
              .apexcharts-legend-series {
                display: flex;
                align-items: center;
                .apexcharts-legend-marker {
                  height: 8px;
                  width: 8px;
                  margin-bottom: 1px;
                  flex-shrink: 0;
                  svg {
                    border-radius: 50%;
                  }
                }
                .apexcharts-legend-text {
                  display: flex;
                  align-items: center;
                  padding-left: 2px;
                  margin-left: 0px;
                  white-space: nowrap;
                  .legend {
                    display: none;
                  }
                }
              }
            }
          }
          g.apexcharts-inner.apexcharts-graphical {
            display: contents;
            g {
              g {
                g {
                  g {
                    path {
                      stroke: var(--card-color);
                      stroke-width: 1px;
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
}
</style>
<style scoped lang="scss">
.briefcase-horizontal {
  display: flex;
  flex-direction: column;
  height: 100%;
  align-items: stretch;
  padding: 0px;
}

.header-line {
  display: flex;
  padding: 15px;
  justify-content: space-between;
  height: 54px;
}

.briefcase-horizontal .header-line,
.briefcase-horizontal .middle-line,
.briefcase-horizontal .footer-line {
  height: 100%;
}

.amount-balance {
  color: #5ac396;
}
.middle-line {
  padding: 0 15px;
}
.footer-line {
  padding: 15px;

  @media screen and (max-width: 461px) {
    padding: 15px 0px;
  }

  a {
    color: var(--color-00);
    text-decoration: none;
    display: flex;
    align-items: center;
    span {
      margin-left: 2px;
    }
  }
}

.button-block {
  display: flex;
  flex-wrap: nowrap;
  margin-top: 15px;
  gap: 6px;

  @media screen and (max-width: 400px) {
    justify-content: space-between;

    > button {
      padding: 0 15px !important;
    }
  }
}

.dropdown-flex {
  flex-grow: 1;
  min-width: 0;
  max-width: 150px;
}
.test-balance {
  margin-bottom: 6px;
}
.test-balance {
  color: var(--color-50);
}
</style>
